import ClassNames from "classnames";
import {
  createElement,
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from "react";
import { shallowEqual } from "react-redux";
import { getPageModules } from "../selectors/modules.js";
import { toggleLegalNav } from "../slices/legalNav.js";
import { useAppDispatch, useAppSelector } from "../store/hooks.js";
import styles from "../stylesheets/components/inline-terms.module.css";
import {
  AllModulesByType,
  BaseModuleProps,
  ColorScheme,
  ContainerQueries,
  Language,
} from "../types/index.js";
import { css } from "../utils/emotion.js";
import { mostModulesByType } from "../utils/mostModulesByType.js";
import { getTranslatedModule, scrollToElSingle } from "../utils/utils.js";
import Icon from "./Icon.js";

interface Props {
  colorScheme: ColorScheme;
  languageId: Language;
  isPreview: boolean;
  queries: ContainerQueries;
  isActive: boolean;
  isFirstOnPage: boolean;
  siteId: string;
}

const getModulesByType = async (): Promise<AllModulesByType> => {
  const { systemPageModulesByType } =
    await import("../utils/systemPageModulesByType.js");

  return { ...mostModulesByType, ...systemPageModulesByType };
};

const InlineTerms: FunctionComponent<Props> = ({
  colorScheme,
  queries,
  isPreview,
  languageId,
}) => {
  const dispatch = useAppDispatch();

  const pageId = useAppSelector(
    (state) => state.legalNav.id && state.pages.byAlias[state.legalNav.id],
  );
  const isOpen = useAppSelector((state) => !!state.legalNav.id);
  const modules = useAppSelector(
    (state) => (pageId ? getPageModules(state.modules, pageId) : []),
    shallowEqual,
  );

  const ref = useRef<HTMLDivElement>(null);
  const [modulesByType, setModulesByType] = useState<AllModulesByType>();

  useEffect(() => {
    pageId && getModulesByType().then((result) => setModulesByType(result));
  }, [pageId]);

  useEffect(() => {
    pageId &&
      ref.current &&
      modulesByType &&
      scrollToElSingle(ref.current, { block: "start", inline: "start" });
  }, [pageId, modulesByType]);

  if (!pageId || !isOpen) return null;

  return (
    <div
      className={ClassNames(
        styles.InlineTerms,
        css({ borderColor: colorScheme.primary.background }),
      )}
      ref={ref}
    >
      <div>
        {modulesByType &&
          modules.map((module) =>
            createElement<BaseModuleProps>(modulesByType[module.type], {
              key: module.id,
              translatedModule: getTranslatedModule(module, languageId),
              pageId,
              queries,
              isPreview,
              isActive: false,
              isFirstOnPage: false,
              activeModuleId: undefined,
            }),
          )}
      </div>
      <button
        className={ClassNames(
          styles.InlineTerms__Close,
          css({
            background: colorScheme.primary.background,
            fill: colorScheme.primary.text,
            color: colorScheme.primary.text,
          }),
        )}
        onClick={() => dispatch(toggleLegalNav({ legalNavId: undefined }))}
      >
        <Icon glyph="close" />
      </button>
    </div>
  );
};

export default InlineTerms;
